import * as React from 'react';
import { find, defaultTo, filter, contains, join, pipe } from 'ramda';
import businessCollectionComponents, {
  defaultStandardDef,
} from '../Features/Delsystem/_Common/Components/BusinessCollectionComponents/businessCollectionComponents';
import StandardObjektVy from '../Features/Delsystem/_Common/Components/BusinessCollectionComponents/StandardObjektVy/StandardObjektVy';
import {
  InternalError,
  internalErrorBusinessComp,
} from '../Components/InternalError/InternalError';
import {
  IObjektVy,
  IObjektVyComp,
  IBusinessCollectionProps,
} from '../Types/ObjektVy';
import { IClientBusinessModel } from '../Types/ClientModelTypes';
import AssociatedIdQuery from '../Components/QueryComponents/AssociatedIdQuery/AssociatedIdQuery';
import { multiVy } from '../Components/MultiVy/MultiVy';
import { findCollectionStatic } from '../Model/avenyStaticModelUtils';
import { AssociatedObjectLink } from '../Features/Delsystem/_Common/Components/BusinessCollectionComponents/AssociatedObjectLink/AssociatedObjectLink';
import { IconType } from '../BaseComponents/Icon/Utils/IconDefinitions';
import { IValidateFn } from '../Features/Delsystem/_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { handleStringDef } from './ObjektVyCaptionUtils';
import { selectedId } from '../Components/MasterDetail/MasterDetailUtils';
import { ICollectionDataType } from '../Features/Delsystem/_Common/Components/BusinessCollectionComponents/Queries/BusinessCollectionQuery';
import { MeQuery } from '../Features/Delsystem/Bokning/Rutiner/Kalender/Queries/MeQuery/_QueryTypes/MeQuery';

import type { JSX } from "react";

/**
Skapar komponenten för en vy enligt def
*/
export const vyDefToComp =
  (bmod: IClientBusinessModel) =>
  (
    vyDef: IObjektVy | string
  ): React.ComponentType<React.PropsWithChildren<IBusinessCollectionProps>> => {
    const vy = handleStringDef(vyDef);
    return vy.def
      ? vy.def(bmod).comp
      : internalErrorBusinessComp(
          'ec2d3790-487b-4a5e-b62e-7600d9c4d22a',
          'def saknas'
        );
  };

export const vyDefsToComp = (bmod: IClientBusinessModel, vyDefs: IObjektVy[]) =>
  multiVy(
    vyDefs.map((vydef: IObjektVy) => ({
      caption: vydef.id || '',
      comp: vydef.def(bmod).comp,
      collectionName: bmod.CollectionName,
    }))
  );

export const demoComp = (collectionName: string, vyDefs: IObjektVy[]) => {
  const collection = findCollectionStatic(collectionName);
  return collection
    ? vyDefsToComp(collection, vyDefs)
    : internalErrorBusinessComp(
        'afc25cb4-41c0-44ce-9347-a0f4c9b89268',
        `collectionName ${collectionName} saknas`
      );
};

/**
Kombinerar två alternativa objektvyer
*/
export const combineViews =
  (caption: string) =>
  (vyer: IObjektVy[]): IObjektVy => ({
    type: 'ObjektVy',
    id: caption,
    info: {},
    def: bmod => ({
      caption: caption,
        comp: multiVy(
          vyer.map((vy: IObjektVy) => ({
            caption: vy.id || '',
            comp: vy.def(bmod).comp,
            collectionName: bmod.CollectionName,
          }))
        ),
    }),
  });

/**
Använder vyn som är registrerad med en viss typ t.ex. 'standard'
*/
export const valjTyp = (vytyp: string): IObjektVy => ({
  type: 'ObjektVy',
  id: vytyp,
  info: {},
  def: (bmod: IClientBusinessModel): IObjektVyComp => {
    const name: string = bmod.CollectionName;
    return pipe(
      businessCollectionComponents,
      find((vy: IObjektVy) => vy.id === vytyp),
      defaultTo(defaultStandardDef),
      (vy: IObjektVy): IObjektVyComp =>
        vy.def
          ? vy.def(bmod)
          : {
              caption: 'fel',
              comp: internalErrorBusinessComp(
                'e85fcbad-de5e-46ba-8577-c4e220687bd9',
                'vy.def saknas'
              ),
            }
    )(name);
  },
});

/**
Vy där man kan välja mellan vissa typer
*/
export const valjTyper = (vytyp: string[]): IObjektVy => ({
  type: 'ObjektVy',
  id: join('-', vytyp),
  info: {},
  def: bmod => {
    const name = bmod.CollectionName;
    const caption = bmod.RootType.SOLabel;
    return pipe(
      businessCollectionComponents,
      filter((vy: IObjektVy) => contains(vy.id, vytyp)) as (
        x: IObjektVy[]
      ) => IObjektVy[],
      combineViews(caption),
      (vy: IObjektVy) => vy.def(bmod)
    )(name);
  },
});

export type SideBySideWidthType =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12;

export interface IFunctionMenuItem {
  id: string;
  caption: string;
  onClick?: () => void;
  disabled?: boolean;
  hidden?: boolean;
  disabledReason?: string;
  iconType?: IconType;
  confirmButtonCaption?: string;
  confirmDialogCaption?: string;
  confirmDialogMessage?: string | JSX.Element;
  selected?: boolean;
}
export interface IUseItemFunctionMenuItemsTypeInput {
  id: string | null;
  collection: IClientBusinessModel;
  associationName?: string;
  disabled?: boolean;
  item?: ICollectionDataType | null;
  me_data?: MeQuery | undefined;
}
export type useItemFunctionMenuItemsType = (
  input: IUseItemFunctionMenuItemsTypeInput,
  customData?: any
) => IFunctionMenuItem[];

export type useBusinessCollectionMenuItemsType = () => IFunctionMenuItem[];

/**
Skapar en vy för standardrutiner utifrån en lista av vyer för flikarna
*/
export const standardVy = (
  tabVyer: IObjektVy[],
  config: {
    useFunktionsMenyItems?: useItemFunctionMenuItemsType;
    sidaVidSida?: boolean;
    sideBySideWidths?: SideBySideWidthType[];
    editEnabled?: boolean;
    validateFns?: IValidateFn[];
    validateHooks?: Array<() => IValidateFn>;
  } = {}
): IObjektVy => ({
  type: 'ObjektVy',
  id: 'standard',
  info: {},
  def: bmod => ({
    caption: bmod.RootType.SOLabel,
    comp: (props: IBusinessCollectionProps) => {
      const funktionsMenyItems = config.useFunktionsMenyItems
        ? config.useFunktionsMenyItems({
            id: selectedId(props.id),
            collection: bmod,
            associationName: '',
          })
        : [];

      return (
        <StandardObjektVy
          collectionName={bmod.CollectionName}
          id={props.id}
          vyer={tabVyer}
          funktionsMenyItems={funktionsMenyItems}
          sidaVidSida={config.sidaVidSida}
          sideBySideWidths={config.sideBySideWidths}
          editImplemented={config.editEnabled}
          config={props.config}
          validateFns={config.validateFns}
          validateHooks={config.validateHooks}
        />
      );
    },
  }),
});

/**
Skapar en generell tabvy.
Än så länge är det en standardvy med möjlighet att välja en annan caption
*/
export const tabVy = (caption: string, tabVyer: IObjektVy[]): IObjektVy => ({
  type: 'ObjektVy',
  id: 'standard',
  info: {},
  def: bmod => ({
    caption: caption,
    comp: (props: IBusinessCollectionProps) => (
      <StandardObjektVy
        collectionName={bmod.CollectionName}
        id={props.id}
        vyer={tabVyer}
        config={props.config}
      />
    ),
  }),
});

const felmeddelandeComp = (felmeddelande: string): IObjektVyComp => ({
  caption: 'Internt fel',
  comp: () => (
    <InternalError
      id="c756bf79-82a8-49fa-9478-7c275ab5d8a9"
      message={felmeddelande}
    />
  ),
});

/**
En vy som visar en "länk" med det associerade objektets header-kolumner, man kan klicka sig vidare till en standardvy över det associerade objektet
*/
export const objektLänk = (associationName: string): IObjektVy => ({
  type: 'ObjektVy',
  id: associationName,
  def: bmod => {
    const assoc = bmod.Associations[associationName];
    const displayLabel = assoc.DisplayLabel;
    const associatedCollection = findCollectionStatic(
      assoc.AssociatedCollectionName
    );
    if (associatedCollection) {
      return {
        caption: displayLabel,
        comp: (props: IBusinessCollectionProps) => {
          return (
            <AssociatedIdQuery
              collectionName={bmod.CollectionName}
              id={selectedId(props.id)}
              associationName={associationName}
            >
              {associatedId => (
                <AssociatedObjectLink
                  businessModel={bmod}
                  assoc={assoc}
                  associatedCollection={associatedCollection}
                  associatedId={associatedId}
                />
              )}
            </AssociatedIdQuery>
          );
        },
      };
    } else {
      return felmeddelandeComp(`association ${associationName} saknas`);
    }
  },
  info: { komponentMedRubrik: true },
});
